import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

// Dense month grid (Monday-first) for custom range selection.
// First click sets the range start, second click sets the end (swapped if reversed).
const MiniCalendar = ({ rangeStart, rangeEnd, onSelectRange }) => {
  const today = new Date()
  const [visible, setVisible] = useState(() => {
    const base = rangeStart || today
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  const monthLabel = visible.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
  const firstDow = (visible.getDay() + 6) % 7 // Monday = 0
  const daysInMonth = new Date(visible.getFullYear(), visible.getMonth() + 1, 0).getDate()

  const cells = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(visible.getFullYear(), visible.getMonth(), d))
  }

  const handleDayClick = (day) => {
    if (rangeStart && !rangeEnd) {
      const [start, end] = day < rangeStart ? [day, rangeStart] : [rangeStart, day]
      onSelectRange(start, end)
    } else {
      onSelectRange(day, null)
    }
  }

  const inRange = (day) =>
    rangeStart && rangeEnd && day > rangeStart && day < rangeEnd

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-xs font-semibold text-primary-800">
        <button
          type="button"
          aria-label="Previous month"
          className="rounded p-0.5 text-gray-400 hover:bg-primary-50 hover:text-primary-700"
          onClick={() => setVisible(new Date(visible.getFullYear(), visible.getMonth() - 1, 1))}
        >
          <ChevronLeft size={14} />
        </button>
        <span>{monthLabel}</span>
        <button
          type="button"
          aria-label="Next month"
          className="rounded p-0.5 text-gray-400 hover:bg-primary-50 hover:text-primary-700"
          onClick={() => setVisible(new Date(visible.getFullYear(), visible.getMonth() + 1, 1))}
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[0.6875rem]">
        {DOW.map((d, i) => (
          <span key={`${d}-${i}`} className="py-1 text-[0.5625rem] font-semibold uppercase tracking-wide text-gray-400">
            {d}
          </span>
        ))}
        {cells.map((day, i) => {
          if (!day) return <span key={`empty-${i}`} />
          const isStart = sameDay(day, rangeStart)
          const isEnd = sameDay(day, rangeEnd)
          let cls = 'cursor-pointer rounded py-1 leading-tight text-gray-600 hover:bg-primary-50'
          if (isStart || isEnd) {
            cls = `cursor-pointer py-1 leading-tight bg-primary-600 font-semibold text-cream-50 ${
              isStart && !rangeEnd ? 'rounded' : isStart ? 'rounded-l' : 'rounded-r'
            }`
          } else if (inRange(day)) {
            cls = 'cursor-pointer py-1 leading-tight bg-primary-100 text-primary-800'
          }
          return (
            <button key={day.toISOString()} type="button" className={cls} onClick={() => handleDayClick(day)}>
              {day.getDate()}
            </button>
          )
        })}
      </div>
      <p className="text-right text-[0.6875rem] font-medium tracking-wide text-gray-400">Custom range</p>
    </div>
  )
}

export default MiniCalendar
